import { Dna } from 'lucide-react';
import { Card } from '@/components/Card';
import { useApi } from '@/hooks/useApi';

interface WorkDNAProfile {
  id: string;
  employee_name: string;
  dimensions: Record<string, number>;
  personality_type?: string;
  work_style: string;
  strengths: string[];
  last_assessment_at: string;
}

export function WorkDNAPage() {
  const { data, loading } = useApi<{ profiles: WorkDNAProfile[] }>('workdna');

  if (loading) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <Dna className="w-5 h-5 text-amber-500" /> WorkDNA Profiles
      </h2>
      {(data?.profiles ?? []).length === 0 ? (
        <Card><p className="text-sm text-muted-foreground">No WorkDNA profiles yet. Create assessments for your team.</p></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(data?.profiles ?? []).map(p => (
            <Card key={p.id}>
              <h3 className="text-sm font-medium text-foreground mb-1">{p.employee_name}</h3>
              {p.personality_type && <p className="text-xs text-amber-500 mb-2">{p.personality_type}</p>}
              <p className="text-xs text-muted-foreground mb-3">{p.work_style}</p>
              {/* Dimension bars */}
              <div className="space-y-1.5">
                {Object.entries(p.dimensions).slice(0, 6).map(([dim, val]) => (
                  <div key={dim} className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-20 capitalize">{dim}</span>
                    <div className="flex-1 bg-surface-3 rounded-full h-1.5">
                      <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: `${val * 10}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-6 text-right">{val}</span>
                  </div>
                ))}
              </div>
              {p.strengths.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {p.strengths.slice(0, 3).map(s => (
                    <span key={s} className="px-1.5 py-0.5 bg-amber-500/10 text-amber-400 rounded text-[10px]">{s}</span>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
